import { ChevronDown, ChevronRight, Layers, FileText, Check } from 'lucide-react';
import { useFormBuilderStore } from '@/features/form-builder/store/form-builder.store';
import { FormPage as Page, FormSection as Section, FormField } from '@/features/application/types/forms';

interface FormStructureNavigatorProps {
  className?: string;
}

// Purpose: Middle-pane hierarchical navigator for pages → sections → fields
const FormStructureNavigator = ({ className = '' }: FormStructureNavigatorProps) => {
  const {
    currentMappingStructure,
    expandedPageId,
    setExpandedPageId,
    activeFieldId,
    setActiveFieldId,
  } = useFormBuilderStore();

  if (!currentMappingStructure) {
    return (
      <aside className={`bg-white border-r border-gray-200 p-4 ${className}`}>
        <div className="text-sm text-gray-500">No form structure loaded.</div>
      </aside>
    );
  }

  const handleTogglePage = (pageId: string) => {
    if (expandedPageId === pageId) {
      setExpandedPageId(null);
    } else {
      setExpandedPageId(pageId);
    }
  };

  const handleSelectField = (fieldId: string, parentPageId: string) => {
    // Ensure only the page containing the selected field is expanded
    if (expandedPageId !== parentPageId) {
      setExpandedPageId(parentPageId);
    }
    setActiveFieldId(fieldId);
  };

  return (
    <aside
      className={`w-80 bg-white border-r border-gray-200 p-4 flex flex-col sticky top-[120px] self-start h-[calc(100vh-120px)] overflow-y-auto ${className}`}
    >
      <h2 className="text-lg font-semibold mb-4 flex items-center">
        <Layers className="h-4 w-4 mr-2" />
        Form Structure
      </h2>
      <ul className="space-y-2">
        {currentMappingStructure.pages.map((page: Page) => {
          const isExpanded = expandedPageId === page.id;
          const totalFields = page.sections.reduce((sum, section) => sum + section.fields.length, 0);
          const mappedFields = page.sections.reduce(
            (sum, section) => sum + section.fields.filter((f) => !!f.fieldApiName).length,
            0
          );
          return (
            <li key={page.id} data-testid={`nav-page-${page.id}`} className="border rounded-md">
              <button
                type="button"
                onClick={() => handleTogglePage(page.id)}
                className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-md"
              >
                <div className="flex items-center gap-2">
                  {isExpanded ? (
                    <ChevronDown className="h-5 w-5" />
                  ) : (
                    <ChevronRight className="h-5 w-5" />
                  )}
                  <span className="font-medium">{page.title}</span>
                  <span className="text-sm text-green-600">({mappedFields}/{totalFields})</span>
                </div>
              </button>

              {isExpanded && (
                <div className="border-t border-gray-200">
                  {page.sections.map((section: Section) => (
                    <div
                      key={section.id}
                      data-testid={`nav-section-${section.id}`}
                      className="p-3"
                    >
                      <div className="text-sm text-gray-700 font-semibold mb-2">{section.title}</div>
                      <ul className="space-y-1">
                        {section.fields.map((field: FormField) => {
                          const isActive = activeFieldId === field.id;
                          return (
                            <li key={field.id} data-testid={`nav-field-${field.id}`}>
                              <button
                                type="button"
                                onClick={() => handleSelectField(field.id, page.id)}
                                className={`w-full text-left px-2 py-1 rounded flex items-center gap-2 transition-colors ${
                                  isActive ? 'bg-blue-100 text-blue-900' : 'hover:bg-gray-100'
                                }`}
                              >
                                <FileText className="h-4 w-4" />
                                <span className="text-sm flex items-center gap-1">
                                  {field.label}
                                  {field.fieldApiName && (
                                    <Check className="h-4 w-4 text-green-600" aria-label="Mapped" />
                                  )}
                                </span>
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </aside>
  );
};

export default FormStructureNavigator;


